import { useLazyQuery, useQuery, gql } from "@apollo/client"
import { offsetLimitPagination } from "@apollo/client/utilities"
import * as Sentry from "@sentry/browser"

const ADMINABLE_USERS_LIMIT = 10

const ADMINABLE_USERS_FIELDS = gql`
  fragment AdminableUsersFields on AdminableUser {
    id
    status
    invite {
      sentAt
      activatedAt
      expiresAt
    }
    firstName
    lastName
    fullName
    email
    avatar {
      key
    }
    locations {
      id
      name
    }
    groups {
      id
      name
    }
    roles {
      id
      friendlyName
    }
    settings {
      notifications {
        simple {
          midday
          evening
          overdue
        }
        custom {
          id
          type
          name
          enabled
          cron
          locations {
            id
          }
        }
      }
    }
    attributes {
      id
      integration
      key
      value
    }
    notes {
      id
      text
      author {
        id
        fullName
        firstName
        lastName
        avatar {
          key
        }
      }
      createdAt
    }
    training {
      percentComplete
      status
      dueAt
      lastActiveAt
      assessments {
        id
        module {
          id
        }
        status
        trainedAt
        trainedBy {
          id
          fullName
        }
        comments
      }
    }
    accreditation {
      status
      statusAt
      accreditations {
        id
        accreditation {
          id
          name
          icon
        }
        uploads {
          id
          key
          fileName
          fileGroup
          fileSize
        }
        warningAt
        expiresAt
        status
        statusAt
        createdAt
      }
    }
    external
    identityProvider {
      id
      name
    }
    unique
    activeAt
  }
`

const ADMINABLE_USERS_QUERY = gql`
  ${ADMINABLE_USERS_FIELDS}
  query AdminableUsers(
    $searchText: String
    $locations: [ID!]
    $roles: [ID!]
    $groups: [ID!]
    $status: [String!]
    $trainingStatus: [String!]
    $accreditationStatus: [String!]
    $offset: Int
    $limit: Int
  ) {
    adminableUsers: adminableUsersV2(
      searchText: $searchText
      locations: $locations
      roles: $roles
      groups: $groups
      status: $status
      trainingStatus: $trainingStatus
      accreditationStatus: $accreditationStatus
      limit: $limit
      offset: $offset
    ) {
      ...AdminableUsersFields
    }
  }
`

const adminableUsersQueryTypePolicies = {
  adminableUsersV2: offsetLimitPagination([
    "searchText",
    "locations",
    "roles",
    "groups",
    "status",
    "trainingStatus",
    "accreditationStatus",
  ]),
}

const loadMore = (result) => async () => {
  try {
    await result.fetchMore({
      variables: {
        ...result.variables,
        offset: result.data.adminableUsers.length,
        limit: ADMINABLE_USERS_LIMIT,
      },
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const useQueryAdminableUsers = (options) => {
  const { variables, ...otherOptions } = options || {}
  const result = useQuery(ADMINABLE_USERS_QUERY, {
    variables: {
      offset: 0,
      limit: ADMINABLE_USERS_LIMIT,
      ...variables,
    },
    fetchPolicy: "cache-and-network",
    ...otherOptions,
  })

  return {
    ...result,
    loadMore: loadMore(result),
  }
}

const useLazyQueryAdminableUsers = (options) => {
  const { variables, ...otherOptions } = options || {}
  const [loadItems, result] = useLazyQuery(ADMINABLE_USERS_QUERY, {
    variables: {
      offset: 0,
      limit: ADMINABLE_USERS_LIMIT,
      ...variables,
    },
    fetchPolicy: "cache-and-network",
    ...otherOptions,
  })

  return [
    loadItems,
    {
      ...result,
      loadMore: loadMore(result),
    },
  ]
}

export {
  useQueryAdminableUsers,
  useLazyQueryAdminableUsers,
  adminableUsersQueryTypePolicies,
  ADMINABLE_USERS_FIELDS,
  ADMINABLE_USERS_LIMIT,
}
